import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CloudArrowUpIcon, 
  DocumentArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * FileUploader Component
 * Drag & Drop file uploader with preview and validation
 * 
 * @param {Object} props
 * @param {Array} props.accept - Accepted file types (e.g., ['.pdf', '.xlsx', '.csv'])
 * @param {Number} props.maxSize - Max file size in bytes (default: 10MB)
 * @param {Boolean} props.multiple - Allow multiple files (default: false)
 * @param {Function} props.onUpload - Callback when files are selected
 * @param {Function} props.onError - Callback for errors
 * @param {String} props.className - Additional CSS classes
 * @param {Boolean} props.disabled - Disable upload
 */
const FileUploader = ({
  accept = ['.pdf', '.xlsx', '.xls', '.csv'],
  maxSize = 10 * 1024 * 1024, // 10MB
  multiple = false,
  onUpload,
  onError,
  className = '',
  disabled = false,
  children
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Validate file
  const validateFile = (file) => {
    // Check file type
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!accept.includes(fileExt)) {
      return t('common.file_uploader.unsupported_type', {
        ext: fileExt,
        accept: accept.join(', '),
      });
    }
    
    // Check file size
    if (file.size > maxSize) {
      return t('common.file_uploader.too_large', {
        size: formatFileSize(file.size),
        max: formatFileSize(maxSize),
      });
    }
    
    return null;
  };
  
  // Handle file selection
  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];
    
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        errors.push({ file: file.name, error });
      } else {
        validFiles.push(file);
      }
    }
    
    // Show errors
    if (errors.length > 0) {
      errors.forEach(({ file, error }) => {
        toast.error(t('common.file_uploader.validation_toast', { file, error }));
      });
      if (onError) onError(errors);
    }
    
    // Add valid files
    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        status: 'pending' // pending, uploading, success, error
      }));
      
      if (multiple) {
        setUploadedFiles(prev => [...prev, ...newFiles]);
      } else {
        setUploadedFiles(newFiles);
      }
      
      if (onUpload) onUpload(validFiles);
    }
  }, [accept, maxSize, multiple, onUpload, onError, t]);
  
  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);
  
  // Handle file input change
  const handleInputChange = useCallback((e) => {
    if (e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);
  
  // Remove file
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update file status
  const updateFileStatus = (index, status, message = null) => {
    setUploadedFiles(prev => prev.map((file, i) => 
      i === index ? { ...file, status, message } : file
    ));
  };
  
  return (
    <div className={`w-full ${className}`}>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-2xl transition-all duration-200
          ${isDragging 
            ? 'border-blue-400 bg-blue-500/10 scale-[1.02]' 
            : 'border-white/20 bg-white/5 hover:border-blue-400/50 hover:bg-white/10'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <input
          type="file"
          accept={accept.join(',')}
          multiple={multiple}
          onChange={handleInputChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          id="file-upload-input"
        />
        
        <label htmlFor="file-upload-input" className="block p-8 cursor-pointer">
          {children || (
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className={`
                p-4 rounded-full transition-colors
                ${isDragging ? 'bg-blue-500/20' : 'bg-white/10'}
              `}>
                <CloudArrowUpIcon className="h-12 w-12 text-blue-400" />
              </div>
              
              <div className="text-center">
                <p className="text-lg font-medium text-white mb-1">
                  {t('common.file_uploader.drag_hint')}
                </p>
                <p className="text-sm text-gray-300">
                  {t('common.file_uploader.browse_hint')}
                </p>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <DocumentArrowUpIcon className="h-4 w-4" />
                <span>{t('common.file_uploader.formats', { max: formatFileSize(maxSize) })}</span>
              </div>
            </div>
          )}
        </label>
      </div>
      
      {/* Uploaded Files List */}
      <AnimatePresence>
        {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2"
          >
            {uploadedFiles.map((fileItem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between p-4 bg-white/5 border border-white/20 rounded-xl"
              >
                <div className="flex items-center gap-3 flex-1">
                  {/* Status Icon */}
                  {fileItem.status === 'pending' && (
                    <DocumentArrowUpIcon className="h-5 w-5 text-blue-400" />
                  )}
                  {fileItem.status === 'uploading' && (
                    <div className="h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  )}
                  {fileItem.status === 'success' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  )}
                  {fileItem.status === 'error' && (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                  )}
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {fileItem.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(fileItem.size)}
                      {fileItem.message && ` · ${fileItem.message}`}
                    </p>
                  </div>
                </div>
                
                {/* Remove Button */}
                <button
                  onClick={() => removeFile(index)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-white" />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FileUploader;

